/**
 * @file DatePicker内で使用される単一の日付入力フィールドコンポーネント。
 *
 * `yyyy-MM-dd`形式のテキスト入力に加え、キーボードの上下キーによる日付の増減、
 * および入力クリア機能を提供します。
 */

import { addDays, format, isValid, parse, subDays } from "date-fns";
import { CalendarIcon, XIcon } from "lucide-react";
import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Button } from "../button";

interface DateRangeInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  iconAriaLabel?: string;
  onIconClick?: () => void;
  onClear?: () => void;
  onValueChange?: (value: string) => void;
}

/**
 * 日付入力フィールドコンポーネント。
 * キーボード操作による日付変更やクリアボタンなどの追加機能を持つ。
 */
const DateRangeInput = React.forwardRef<HTMLInputElement, DateRangeInputProps>(
  (
    {
      className,
      iconAriaLabel = "Open calendar",
      onIconClick,
      onClear,
      value,
      onValueChange,
      ...props
    },
    ref,
  ) => {
    const showClearButton = value && onClear && !props.disabled;
    const showIconButton = onIconClick && !props.disabled;

    /**
     * キーボードの上下矢印キーが押された際の処理。
     * 入力値が有効な日付の場合、日付を1日増減させる。
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
        return;
      }
      if (typeof value !== "string" || !value) {
        return;
      }
      const currentDate = parse(value, "yyyy-MM-dd", new Date());
      if (!isValid(currentDate)) {
        return;
      }
      e.preventDefault();
      const newDate =
        e.key === "ArrowUp" ? addDays(currentDate, 1) : subDays(currentDate, 1);
      const newValue = format(newDate, "yyyy-MM-dd");
      onValueChange?.(newValue);
    };

    return (
      <div className="relative flex items-center">
        <Input
          ref={ref}
          value={value}
          className={cn(
            "bg-background",
            showClearButton && showIconButton
              ? "pr-16"
              : showClearButton || showIconButton
                ? "pr-8"
                : undefined,
            className,
          )}
          onKeyDown={handleKeyDown}
          onChange={(e) => onValueChange?.(e.target.value)}
          {...props}
        />
        {showClearButton && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "absolute h-7 w-7 text-muted-foreground opacity-50 hover:opacity-100",
              showIconButton ? "right-8" : "right-1",
            )}
            onClick={(e) => {
              e.stopPropagation();
              onClear?.();
            }}
            aria-label="Clear input"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        )}
        {showIconButton && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 h-7 w-7 text-muted-foreground opacity-70 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onIconClick?.();
            }}
            aria-label={iconAriaLabel}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    );
  },
);

DateRangeInput.displayName = "DateRangeInput";

export { DateRangeInput };
